import { action, computed, flow, makeObservable, observable } from 'mobx';

import { ApiError, OperationAbortedError } from 'src/packages/errors';
import { debounce } from 'src/packages/utils/debounce';
import { equalsDeep } from 'src/packages/utils/equals-deep';

import type { UserInfo } from '../auth-service/user-service';
import type { INotificationsService } from '../notifications-service';
import type { GridFilterModel } from '@repo/go-table';
import type { AxiosInstance } from 'axios';

import { TableSettingsServiceApi } from './table-settings-service.api';

export type TTableSortItem = {
  field: string;
  sort: 'asc' | 'desc';
};

export type TTablePinModel = {
  left?: string[];
  right?: string[];
};

export type TFilterItem = {
  field: string;
  operator: string;
  id: string;
  value?: string | number;
};

export type TTableSettingsObject = {
  columnsVisibility?: Record<string, boolean>;
  pinnedColumns?: TTablePinModel;
  sortingModel?: TTableSortItem[];
  columnsOrderModel?: string[];
  columnsWidth?: Record<string, number>;
  filterModel?: GridFilterModel;
  fieldValues?: Record<string, unknown>;
};

export type TTableSettings = {
  ownerUserId?: string;
  settings: Record<string, TTableSettingsObject>;
};

interface IUserService {
  getUserData(): UserInfo | null;
}

export class TableSettingsService {
  private readonly notifications: INotificationsService;
  private readonly userService: IUserService;
  private readonly api: TableSettingsServiceApi;

  @observable.deep private settings: TTableSettings | null = null;

  @observable isSettingsLoading = false;

  constructor(agent: AxiosInstance, userService: IUserService, notificationsService: INotificationsService) {
    this.userService = userService;
    this.notifications = notificationsService;
    this.api = new TableSettingsServiceApi(agent);
    makeObservable(this);
  }

  private async createSettings(): Promise<TTableSettings | null> {
    const userData = this.userService.getUserData();

    if (!userData?.sub) {
      return null;
    }

    await this.api.createSettings({ ownerUserId: userData.sub, settings: {} });

    return this.api.loadSettings();
  }

  private async getSettings(): Promise<TTableSettings | null> {
    try {
      const settings = await this.api.loadSettings();

      if (settings) {
        return settings;
      } else {
        return await this.createSettings();
      }
    } catch (e) {
      if (e instanceof ApiError && e.statusCode === 404) {
        return await this.createSettings();
      }

      throw e;
    }
  }

  @computed
  get tableSettings(): Record<string, TTableSettingsObject> | null {
    if (!this.settings) {
      return null;
    }

    return this.settings.settings;
  }

  @flow.bound
  async *getOrCreateSettings() {
    if (this.settings) {
      return this.settings.settings;
    }

    this.isSettingsLoading = true;

    try {
      const settings = await this.getSettings();
      yield;

      if (settings) {
        this.settings = settings;
      }

      return this.settings?.settings;
    } catch (e) {
      yield;

      if (e instanceof OperationAbortedError) {
        return this.settings?.settings;
      }

      this.settings = {
        settings: {},
      };

      if (e instanceof ApiError) {
        if (e.message) {
          this.notifications.showErrorMessage(e.message);
        }
      }

      this.notifications.showErrorMessageT('settings:table.errors.failedToLoadSettings');
    } finally {
      yield;
      this.isSettingsLoading = false;
    }

    return null;
  }

  @flow.bound
  private async *_updateSettingsReq(settings: TTableSettings, signal?: AbortSignal) {
    try {
      await this.api.updateSettings(settings, signal);
      yield;

      return this.getOrCreateSettings();
    } catch (e) {
      yield;
      if (e instanceof OperationAbortedError) {
        return null;
      }
      if (e instanceof ApiError && e.message) {
        this.notifications.showErrorMessage(e.message);
        return null;
      }
      this.notifications.showErrorMessageT('settings:table.errors.failedToUpdateTableSettings');
    }
    return null;
  }

  private debouncedUpdateSettingsReq = debounce(
    (settings: TTableSettings, signal?: AbortSignal) => this._updateSettingsReq(settings, signal),
    1000,
  );

  @action.bound
  updateSettings(directoryName: string, settings: TTableSettingsObject) {
    const newSettings = {
      ...this.settings,
      settings: {
        ...this.settings?.settings,
        [directoryName]: settings,
      },
    };

    if (equalsDeep(newSettings, this.settings)) {
      return;
    }

    this.settings = newSettings;

    return this.debouncedUpdateSettingsReq(newSettings);
  }

  @action.bound
  resetSettings(directoryName?: string): void {
    if (!this.settings) {
      return;
    }

    const settingsObject = (() => {
      if (!directoryName) {
        return {};
      }

      const { [directoryName]: _, ...rest } = this.settings.settings;

      return rest;
    })();

    const newSettings = {
      ...this.settings,
      settings: settingsObject,
    };

    this.settings = newSettings;

    this.debouncedUpdateSettingsReq(newSettings);
  }

  init = () => {
    this.getOrCreateSettings();
  };
}
